import axios from 'axios';
import { auth } from './firebase';
import { shouldUseLocalBackend, API_TIMEOUT_MS } from '../config/systemConfig';

export const API_BASE_URL = shouldUseLocalBackend 
  ? 'http://localhost:8080'
  : (import.meta.env.VITE_API_URL || 'https://atelier-persopol-2.uc.r.appspot.com');

const getAuthHeader = async () => {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
};

export const consumeCredit = async () => {
  const headers = await getAuthHeader();
  try {
    const response = await axios.post(`${API_BASE_URL}/credits/consume`, {}, { headers });
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.status === 402) {
      const err: any = new Error("Insufficient credits");
      err.code = "insufficient_balance";
      throw err;
    }
    throw error;
  }
};

export const grantInitialCredits = async () => {
  const headers = await getAuthHeader();
  const response = await axios.post(`${API_BASE_URL}/users/grant-initial-credits`, {}, { headers });
  return response.data;
};

export const transformImage = async (
  contentImageBase64: string, 
  styleImageBase64: string, 
  styleImagesBase64: string[] = [], 
  genreName: string = '',
  styleImageUrl?: string,
  styleImageUrls?: string[]
) => {
  const headers = await getAuthHeader();
  
  // Ensure content image is raw base64
  const contentClean = contentImageBase64.includes(',') 
    ? contentImageBase64.split(',')[1] 
    : contentImageBase64;

  // Ensure style image is raw base64 (if it was passed as data URL)
  const styleClean = styleImageBase64.includes(',')
    ? styleImageBase64.split(',')[1]
    : styleImageBase64;

  // Ensure list items are raw base64
  const styleImagesClean = styleImagesBase64.map(s => s.includes(',') ? s.split(',')[1] : s);

  const response = await axios.post(`${API_BASE_URL}/transform/4o`, {
    content_image_base64: contentClean,
    style_image_base64: styleClean,
    style_images_base64: styleImagesClean,
    genre_name: genreName,
    style_image_url: styleImageUrl,
    style_image_urls: styleImageUrls
  }, { 
    headers,
    timeout: API_TIMEOUT_MS, // 40 minutes timeout to match backend
    maxBodyLength: Infinity,
    maxContentLength: Infinity
  });
  return response.data;
};

export const editImage = async (imageBase64: string, instruction: string) => {
  const headers = await getAuthHeader();
  const response = await axios.post(`${API_BASE_URL}/image/edit`, {
    candidate_image_base64: imageBase64.split(',')[1],
    instruction,
  }, { headers });
  return response.data;
};



export interface RegionsConfig {
  supportedCurrencies: string[];
  zeroDecimalCurrencies: string[];
  supportedCurrenciesMarkups: Record<string, string>;
  supportedCountries: Record<string, string>;
  countryToCurrency: Record<string, string>;
}

export const getRegionsConfig = async (): Promise<RegionsConfig> => {
  // Use public endpoint, no auth required usually for config, but headers is fine
  const response = await axios.get(`${API_BASE_URL}/config/regions`);
  return response.data;
};

export interface TemplateConfig {
  canvas: string[];
  acrylic: string[];
  classicMattePaper: string[];
  fineArt: string[];
  firstPriority: string[];
  secondPriority: string[];
  productGenerationTimeoutHours?: number;
}

export const getTemplates = async (): Promise<TemplateConfig> => {
  const headers = await getAuthHeader();
  try {
    const response = await axios.get(`${API_BASE_URL}/gelato/templates`, { headers });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch templates, using defaults", error);
    // Fallback or rethrow? Let's rethrow to handle in UI or return empty/default
    throw error;
  }
};

export interface GelatoPriceEntry {
  productUid: string;
  country?: string;
  quantity: number;
  price: number;
  currency?: string;
  pageCount?: number;
}

export interface GeneratedProduct {
  id: string;
  title: string;
  description: string;
  previewUrl: string;
  productPrice: GelatoPriceEntry | null;
  currency: string;
  templateId: string;
  productUid: string;
}

export const generateProducts = async (
  imageUrl: string,
  creationHash: string,
  firstPriorityTemplateIds: string[],
  secondPriorityTemplateIds: string[],
  country: string = 'US',
  currency: string = 'USD',
  timeoutSeconds: number = 120
): Promise<GeneratedProduct[]> => {
  const headers = await getAuthHeader();
  
  const response = await axios.post(`${API_BASE_URL}/gelato/products:generate`, {
    template_ids: firstPriorityTemplateIds,
    secondary_template_ids: secondPriorityTemplateIds,
    image_url: imageUrl,
    country,
    currency,
    creation_hash: creationHash,
    timeout_seconds: timeoutSeconds
  }, { 
    headers,
    timeout: (timeoutSeconds + 30) * 1000 // Add buffer to timeout
  });
  
  // Handle 202 Accepted (background processing)
  if (response.status === 202) {
    const error: any = new Error("Product generation is processing in background");
    error.isProcessing = true;
    throw error;
  }
  
  return response.data as GeneratedProduct[];
};

export const fetchProductPrice = async (
  productUid: string, 
  country: string = 'US', 
  currency: string = 'USD'
): Promise<{ productUid: string, country: string, unitPrice: number, currency: string }> => {
  const headers = await getAuthHeader();
  const response = await axios.get(`${API_BASE_URL}/gelato/prices/unit`, {
    headers,
    params: { productUid, country, currency }
  });
  return response.data;
};

export interface GelatoShippingAddress {
  firstName: string;
  lastName: string;
  companyName?: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postCode: string;
  country: string; // ISO 2-letter code
  state?: string;
  email?: string;
  phone: string;
}

export interface GelatoCreateOrderRequest {
  orderType: string; // "order"
  orderReferenceId: string;
  customerReferenceId?: string | null;
  currency: string;
  items: Array<{
    itemReferenceId: string;
    productUid: string;
    files: Array<{ url: string }>;
    quantity: number;
  }>;
  shipmentMethodUid: string;
  shippingAddress: GelatoShippingAddress;
  returnAddress?: any;
  metadata?: Array<{ key: string; value: string }>;
}

export interface StripeProduct {
  id: string;
  name: string;
  description: string | null;
  price: {
    id: string;
    unit_amount: number;
    currency: string;
  } | null;
}

export const fetchStripeProduct = async (productId: string): Promise<StripeProduct> => {
  const headers = await getAuthHeader();
  const response = await axios.get(`${API_BASE_URL}/payments/products/${productId}`, { headers });
  return response.data;
};

export const createCheckoutSession = async (
  amountCents: number,
  currency: string,
  productName: string,
  successPath: string = '/order',
  priceId?: string,
  productId?: string,
  customerEmail?: string
) => {
  const headers = await getAuthHeader();
  const successUrl = `${window.location.origin}${successPath}`;
  const cancelUrl = `${window.location.origin}/`; // Return to home on cancel

  const response = await axios.post(`${API_BASE_URL}/payments/checkout`, {
    amount_cents: amountCents,
    currency,
    product_name: productName,
    price_id: priceId,
    product_id: productId,
    success_url: successUrl,
    cancel_url: cancelUrl,
    customer_email: customerEmail
  }, { headers });
  return response.data as { url: string; session_id: string };
};

export const finalizeOrder = async (
  sessionId: string,
  orderRequest: GelatoCreateOrderRequest
) => {
  const headers = await getAuthHeader();
  const response = await axios.post(`${API_BASE_URL}/gelato/orders:deferred`, {
    session_id: sessionId,
    order_request: orderRequest
  }, { headers });
  return response.data;
};

export const finalizeCreditPurchase = async (sessionId: string) => {
  const headers = await getAuthHeader();
  const response = await axios.post(`${API_BASE_URL}/payments/finalize-credits`, {
    session_id: sessionId
  }, { headers });
  return response.data;
};

export interface OrderItem {
  itemReferenceId: string;
  productUid: string;
  files: { url: string }[];
  quantity: number;
}

export interface Order {
  id: string;
  orderReferenceId: string;
  customerReferenceId?: string;
  items: OrderItem[];
  fulfillmentStatus?: string;
  financialStatus?: string;
  shipment?: any;
  createdAtServer?: string;
  updatedAt?: string;
  orderedAt?: string;
  currency?: string;
  channel?: string;
  shippingAddress?: GelatoShippingAddress;
  returnAddress?: any;
  billingEntity?: any;
  receipts?: any[];
  metadata?: Array<{ key: string; value: string }>;
}

export const getUserOrders = async (refresh: boolean = false): Promise<Order[]> => {
  const headers = await getAuthHeader();
  const response = await axios.get(`${API_BASE_URL}/orders`, { 
    headers,
    params: { refresh }
  });
  return response.data;
};
